import React from 'react';
import PropTypes from 'prop-types';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import { useQueryProvider } from '@/modules/QueryProvider';
import { fetchConstituent } from '@/services/constituentServices';
import useLifecycleBoard from '../lib/hooks/useLifecycleBoard';

const LifecycleBoardContext = React.createContext();

export const useLifecycleBoardContext = () =>
  React.useContext(LifecycleBoardContext);

export const Actions = {
  ADD_CONSTITUENT: 'ADD_CONSTITUENT',
  MOVE_CONSTITUENT: 'MOVE_CONSTITUENT',
  REMOVE_CARD: 'REMOVE_CARD',
  UPDATE_COLUMN: 'UPDATE_COLUMN',
};

const initColumn = () => ({ total: 0, items: [[]] });

export const reducer = (state, action) => {
  const { column, constituent, from, items, to, total, type } = action;
  let newState;
  switch (type) {
    case Actions.ADD_CONSTITUENT:
      newState = { [column]: initColumn(), ...state };
      newState[column].total += 1;
      newState[column].items[newState[column].items.length - 1].push(
        constituent,
      );
      break;
    case Actions.MOVE_CONSTITUENT:
      newState = {
        [from]: initColumn(),
        [to]: initColumn(),
        ...state,
      };
      newState[from].items = newState[from].items.map((set = []) =>
        set.filter(
          ({ constituentId }) => constituentId !== constituent.constituentId,
        ),
      );
      newState[from].total = Math.max(newState[from].total - 1, 0);
      newState[to].items[newState[to].items.length - 1].push(constituent);
      newState[to].total += 1;
      break;
    case Actions.REMOVE_CARD: {
      newState = { [column]: initColumn(), ...state };
      newState[column].items = newState[column].items.map((set = []) =>
        set.filter(
          ({ constituentId }) => constituentId !== constituent.constituentId,
        ),
      );

      newState[column].total = Math.max(newState[column].total - 1, 0);
      break;
    }
    case Actions.UPDATE_COLUMN:
      newState = {
        ...state,
        [column]: { total, items },
      };
      break;
    default:
      return state;
  }
  return newState;
};

export function LifecycleBoardProvider({ boardId, children }) {
  const [state, dispatch] = React.useReducer(reducer, {});
  const { query, updateQuery } = useQueryProvider();
  const ownerOrgId = useOwnerOrgId();
  const {
    addColumn,
    addConstituent,
    columns,
    data: board,
    error,
    removeColumn,
    updateColumnInfo,
    updateConstituent: updateConstituentPosition,
  } = useLifecycleBoard(boardId);
  const getColumn = React.useCallback(
    (column) => state[column] || initColumn(),
    [state],
  );
  const getConstituent = React.useCallback((id) =>
    Object.values(state)
      .reduce((arr, { items }) => [...arr, ...items.flat()], [])
      .find(({ constituentId }) => id === constituentId),
  );
  const updateColumn = (column, update = {}) =>
    dispatch({ type: Actions.UPDATE_COLUMN, column, ...update });
  const moveConstituent = (constituent, from, to) => {
    dispatch({ type: Actions.MOVE_CONSTITUENT, constituent, from, to });
  };
  const removeConstituent = (constituent, column) => {
    dispatch({ type: Actions.REMOVE_CARD, constituent, column });
  };
  // This method is only being called behind the feature flag.
  const addConstituentToColumn = async (constituentId, column) => {
    addConstituent({
      constituentId,
      columnId: column,
    });
    const res = await fetchConstituent({ constituentId, ownerOrgId });
    const { constituent } = res.data;
    dispatch({ type: Actions.ADD_CONSTITUENT, constituent, column });
  };

  const value = React.useMemo(
    () => ({
      addColumn,
      addConstituentToColumn,
      board,
      columns,
      error,
      filters: query,
      getColumn,
      getConstituent,
      moveConstituent,
      removeColumn,
      removeConstituent,
      updateColumn,
      updateColumnInfo,
      updateConstituentPosition,
      updateFilters: updateQuery,
    }),
    [
      addColumn,
      addConstituentToColumn,
      board,
      columns,
      error,
      getColumn,
      getConstituent,
      moveConstituent,
      query,
      removeColumn,
      removeConstituent,
      updateColumn,
      updateColumnInfo,
      updateConstituentPosition,
      updateQuery,
    ],
  );

  return (
    <LifecycleBoardContext.Provider value={value}>
      {children}
    </LifecycleBoardContext.Provider>
  );
}

LifecycleBoardProvider.propTypes = {
  boardId: PropTypes.string,
  children: PropTypes.node.isRequired,
};

LifecycleBoardProvider.defaultProps = {
  boardId: null,
};

export default LifecycleBoardContext;
